import AuthAPI from '../api/auth';
import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import DashboardAudioNotificationHelper from './AudioAlerts/DashboardAudioNotificationHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { useImpersonation } from 'dashboard/composables/useImpersonation';

const { isImpersonating } = useImpersonation();

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    const { websocketURL = '' } = window.chatwootConfig || {};
    super(app, pubsubToken, websocketURL);
    this.CancelTyping = [];
    this.streamingTimers = {};
    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'message.streaming.start': this.onMessageStreamingStart,
      'message.streaming': this.onMessageStreaming,
      'message.streaming.process': this.onMessageStreamingProcess,
      'message.streaming.thinking': this.onMessageStreamingThinking,
      'conversation.created': this.onConversationCreated,
      'conversation.status_changed': this.onStatusChange,
      'user:logout': this.onLogout,
      'page:reload': this.onReload,
      'assignee.changed': this.onAssigneeChanged,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.contact_changed': this.onConversationContactChange,
      'presence.update': this.onPresenceUpdate,
      'contact.deleted': this.onContactDelete,
      'contact.updated': this.onContactUpdate,
      'conversation.mentioned': this.onConversationMentioned,
      'notification.created': this.onNotificationCreated,
      'notification.deleted': this.onNotificationDeleted,
      'notification.updated': this.onNotificationUpdated,
      'conversation.read': this.onConversationRead,
      'conversation.updated': this.onConversationUpdated,
      'account.cache_invalidated': this.onCacheInvalidate,
      'copilot.message.created': this.onCopilotMessageCreated,
      'pilot.copilot.message.created': this.onPilotCopilotMessageCreated,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT);
  };

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_DISCONNECT);
  };

  isAValidEvent = data => {
    return this.app.$store.getters.getCurrentAccountId === data.account_id;
  };

  onMessageUpdated = data => {
    const { conversation_id: conversationId } = data;
    const allConversations = this.app.$store.state.conversations.allConversations;
    const conversation = allConversations.find(c => c.id === conversationId);
    const existingMessage = conversation?.messages?.find(m => m.id === data.id);

    if (existingMessage?.status === 'streaming') {
      const updates = { message_id: data.id, conversation_id: conversationId };
      let needsUpdate = false;

      if (data.content && existingMessage.content !== data.content) {
        updates.content = data.content;
        updates.process_label = '';
        updates.process_status = null;
        needsUpdate = true;
      }

      if (data.status && existingMessage.status !== data.status) {
        updates.status = data.status;
        needsUpdate = true;
      }

      if (data.attachments !== undefined) {
        updates.attachments = data.attachments;
        needsUpdate = true;
      }

      if (needsUpdate) {
        this.app.$store.dispatch('updateStreamingMessage', updates);
      }
      return;
    }

    this.app.$store.dispatch('updateMessage', data);
  };

  onPresenceUpdate = data => {
    if (isImpersonating.value) return;
    this.app.$store.dispatch('contacts/updatePresence', data.contacts);
    this.app.$store.dispatch('agents/updatePresence', data.users);
    this.app.$store.dispatch('setCurrentUserAvailability', data.users);
  };

  onConversationContactChange = payload => {
    const { meta = {}, id: conversationId } = payload;
    const { sender } = meta || {};
    if (conversationId) {
      this.app.$store.dispatch('updateConversationContact', {
        conversationId,
        ...sender,
      });
    }
  };

  onAssigneeChanged = payload => {
    const { id } = payload;
    if (id) {
      this.app.$store.dispatch('updateConversation', payload);
    }
    this.fetchConversationStats();
  };

  onConversationCreated = data => {
    this.app.$store.dispatch('addConversation', data);
    this.fetchConversationStats();
  };

  onConversationRead = data => {
    this.app.$store.dispatch('updateConversation', data);
  };

  // eslint-disable-next-line class-methods-use-this
  onLogout = () => AuthAPI.logout();

  onMessageCreated = data => {
    // 跳过 streaming 消息：由 onMessageStreamingStart 处理创建
    if (data.status === 'streaming') return;

    const {
      conversation: { last_activity_at: lastActivityAt },
      conversation_id: conversationId,
    } = data;

    // 检查消息是否已由流式事件添加到 store
    const allConversations = this.app.$store.state.conversations.allConversations;
    const conversation = allConversations.find(c => c.id === conversationId);
    const existingMessage = conversation?.messages?.find(m => m.id === data.id);

    if (existingMessage) {
      // 消息已存在（流式添加），做精准字段补丁，不替换整条消息体
      // 避免经 Sidekiq 的 push_event_data 序列化不一致导致 sender/variant 错误
      const updates = { message_id: data.id, conversation_id: conversationId };
      let needsUpdate = false;

      // 兜底：done:true 丢失导致定时器标记为 failed 时，message.created 修正为 sent
      if (existingMessage.status === 'streaming' || existingMessage.status === 'failed') {
        updates.status = 'sent';
        needsUpdate = true;
      }
      // 流式阶段可能先显示过渡话术，message.created 到达时应以最终内容为准
      if (data.content && existingMessage.content !== data.content) {
        updates.content = data.content;
        updates.process_label = '';
        updates.process_status = null;
        needsUpdate = true;
      }

      if (data.attachments !== undefined) {
        updates.attachments = data.attachments;
        needsUpdate = true;
      }

      if (needsUpdate) {
        this.app.$store.dispatch('updateStreamingMessage', updates);
      }
      this.app.$store.dispatch('updateConversationLastActivity', {
        lastActivityAt,
        conversationId,
      });
      return;
    }

    // 非流式消息：正常处理
    DashboardAudioNotificationHelper.onNewMessage(data);
    this.app.$store.dispatch('addMessage', data);
    this.app.$store.dispatch('updateConversationLastActivity', {
      lastActivityAt,
      conversationId,
    });
  };

  // 流式输出：收到 start 事件，data 格式与 message.created 一致（来自 push_event_data）
  onMessageStreamingStart = data => {
    this.app.$store.dispatch('addMessage', data);
    this.streamingTimers[data.id] = setTimeout(() => {
      // 超时兜底：如果消息仍为 streaming，标记为 failed
      this.app.$store.dispatch('updateStreamingMessage', {
        message_id: data.id,
        conversation_id: data.conversation_id,
        status: 'failed',
      });
      delete this.streamingTimers[data.id];
    }, 30000);
  };

  // 流式输出：收到 chunk，更新气泡内容（累积内容，直接替换）
  onMessageStreaming = data => {
    const { message_id: messageId, conversation_id: conversationId, account_conversation_id: accountConversationId, content, done } = data;
    // 优先使用内部 ID（account_conversation_id）查找 Vuex 会话，fallback 到 display_id
    const internalConvId = accountConversationId || conversationId;
    if (done) {
      if (data.error) {
        // 错误：清除定时器，立即标记为 failed
        if (this.streamingTimers?.[messageId]) {
          clearTimeout(this.streamingTimers[messageId]);
          delete this.streamingTimers[messageId];
        }
        const allConversations = this.app.$store.state.conversations.allConversations;
        const conversation = allConversations.find(c => c.id === internalConvId);
        const currentMessage = conversation?.messages?.find(m => m.id === messageId);
        const currentContent = currentMessage?.content || '';
        this.app.$store.dispatch('updateStreamingMessage', {
          message_id: messageId,
          conversation_id: internalConvId,
          content: currentContent + '\n\n（回复中断）',
          status: 'failed',
          process_label: '',
          process_status: null,
        });
      } else {
        // 正常完成：如果 done 事件已携带最终内容，先落到气泡上，再更新状态
        const updates = {
          message_id: messageId,
          conversation_id: internalConvId,
          status: 'sent',
        };
        if (content !== undefined) {
          updates.content = content;
          updates.process_label = '';
          updates.process_status = null;
        }
        this.app.$store.dispatch('updateStreamingMessage', updates);
        if (this.streamingTimers?.[messageId]) {
          clearTimeout(this.streamingTimers[messageId]);
          delete this.streamingTimers[messageId];
        }
      }
      return;
    }
    this.app.$store.dispatch('updateStreamingMessage', {
      message_id: messageId,
      conversation_id: internalConvId,
      content: content || '',
      process_label: '',
      process_status: null,
    });
  };

  onMessageStreamingProcess = data => {
    const {
      message_id: messageId,
      conversation_id: conversationId,
      account_conversation_id: accountConversationId,
      state,
      label,
    } = data;
    const internalConvId = accountConversationId || conversationId;
    this.app.$store.dispatch('updateStreamingMessage', {
      message_id: messageId,
      conversation_id: internalConvId,
      process_status: state,
      process_label: label || '',
    });
  };

  // AI thinking content: update thinking_content on streaming message
  onMessageStreamingThinking = data => {
    const { message_id: messageId, conversation_id: conversationId, account_conversation_id: accountConversationId, thinking_content: thinkingContent } = data;
    const internalConvId = accountConversationId || conversationId;
    this.app.$store.dispatch('updateStreamingMessage', {
      message_id: messageId,
      conversation_id: internalConvId,
      thinking_content: thinkingContent || '',
    });
  };

  // eslint-disable-next-line class-methods-use-this
  onReload = () => window.location.reload();

  onStatusChange = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onConversationUpdated = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onTypingOn = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/create', {
      conversationId,
      user,
    });
    this.initTimer({ conversation, user });
  };

  onTypingOff = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/destroy', {
      conversationId,
      user,
    });
  };

  onConversationMentioned = data => {
    this.app.$store.dispatch('addMentions', data);
  };

  clearTimer = conversationId => {
    const timerEvent = this.CancelTyping[conversationId];

    if (timerEvent) {
      clearTimeout(timerEvent);
      this.CancelTyping[conversationId] = null;
    }
  };

  initTimer = ({ conversation, user }) => {
    const conversationId = conversation.id;
    // Turn off typing automatically after 30 seconds
    this.CancelTyping[conversationId] = setTimeout(() => {
      this.onTypingOff({ conversation, user });
    }, 30000);
  };

  // eslint-disable-next-line class-methods-use-this
  fetchConversationStats = () => {
    emitter.emit('fetch_conversation_stats');
  };

  onContactDelete = data => {
    this.app.$store.dispatch(
      'contacts/deleteContactThroughConversations',
      data.id
    );
    this.fetchConversationStats();
  };

  onContactUpdate = data => {
    this.app.$store.dispatch('contacts/updateContact', data);
  };

  onNotificationCreated = data => {
    this.app.$store.dispatch('notifications/addNotification', data);
  };

  onNotificationDeleted = data => {
    this.app.$store.dispatch('notifications/deleteNotification', data);
  };

  onNotificationUpdated = data => {
    this.app.$store.dispatch('notifications/updateNotification', data);
  };

  onCopilotMessageCreated = data => {
    this.app.$store.dispatch('copilotMessages/upsert', data);
  };

  onPilotCopilotMessageCreated = data => {
    this.app.$store.dispatch('pilotCopilotMessages/upsert', data);
  };

  onCacheInvalidate = data => {
    const keys = data.cache_keys;
    this.app.$store.dispatch('labels/revalidate', { newKey: keys.label });
    this.app.$store.dispatch('inboxes/revalidate', { newKey: keys.inbox });
    this.app.$store.dispatch('teams/revalidate', { newKey: keys.team });
  };
}

export default {
  init(store, pubsubToken) {
    return new ActionCableConnector({ $store: store }, pubsubToken);
  },
};
